import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  IconButton,
  useTheme,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import DatePicker2 from "../../../components/common/textFields/DatePicker2";
import dayjs from "dayjs";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { OrderProducts } from "../../../../models/TaskTracker/OrderProducts";
import {
  UnpaidSampleProduct,
  UnpaidSamplesReturnInfoUpdate,
} from "../../../../models/ProjectManagement/UnpaidSample";
import { Dayjs } from "dayjs";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import AppTableHeaderCell from "../../../components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../components/common/pages/AppTableRowCell";
import MediaAssetsUpload from "../../../components/common/upload/MediaAssetsUpload";
import ImageAPI from "../../../../api/ImageAPI";
import { ImageData } from "../../../../models/Image/Image";
import FileUploadIcon from "../../../../assets/fileuploadicon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";

interface UnpaidSamplePickupUpdatePopupProps {
  open: boolean;
  existingPickupData: UnpaidSamplesReturnInfoUpdate | null;
  productdata: OrderProducts[];
  onClose: () => void;
  onSave: (payload: UnpaidSamplesReturnInfoUpdate, pickup_id: number) => void;
  onDeleteImage: (image: ImageData) => void;
  orderId: number;
}

const UnpaidSamplePickupUpdatePopup: React.FC<
  UnpaidSamplePickupUpdatePopupProps
> = ({
  open,
  onClose,
  onSave,
  existingPickupData,
  productdata,
  onDeleteImage,
  orderId,
}) => {
  const theme = useTheme();
  const { projectManagementStore, userStore } = useStores();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const sortedProductData = useMemo(() => {
    return [...productdata].sort((a, b) => {
      const aReceivedQty =
        projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products?.find(
          (p) => p.id === a.id,
        )?.quantity || 0;
      const bReceivedQty =
        projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products?.find(
          (p) => p.id === b.id,
        )?.quantity || 0;

      const aActualQty = a.quantity || 0;
      const bActualQty = b.quantity || 0;

      const aFullyReceived = aReceivedQty >= aActualQty;
      const bFullyReceived = bReceivedQty >= bActualQty;

      // If one is fully received and other is not, put the not fully received first
      if (aFullyReceived && !bFullyReceived) return 1;
      if (!aFullyReceived && bFullyReceived) return -1;

      return 0;
    });
  }, [
    productdata,
    projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products,
  ]);

  useEffect(() => {
    if (open && existingPickupData) {
      projectManagementStore.setUnpaidSamplePickupUpdateFormData({
        actual_pickup_date: existingPickupData.actual_pickup_date
          ? dayjs(existingPickupData.actual_pickup_date).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          : "",
        actual_pack_received_date: existingPickupData.actual_pack_received_date
          ? dayjs(existingPickupData.actual_pack_received_date).format(
              "YYYY-MM-DD HH:mm:ss",
            )
          : "",
        product_remarks: existingPickupData.product_remarks || "",
        image_urls: existingPickupData.image_urls || [],
      });

      if (productdata && productdata.length > 0) {
        const initialProducts: UnpaidSampleProduct[] = productdata.map(
          (product) => {
            // const existingProduct = existingPickupData.products?.find(
            //   (p: UnpaidSampleProduct) => p.id === product.id,
            // );

            // Calculate default return quantity (actual - received)
            const receivedQty =
              projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products?.find(
                (p) => p.id === product.id,
              )?.quantity || 0;
            const actualQty = product.quantity || 0;
            const defaultReturnQty = Math.max(0, actualQty - receivedQty);

            return {
              id: product.id || 0,
              quantity: defaultReturnQty,
              is_selected: false,
              is_archived: false,
            };
          },
        );

        projectManagementStore.setselectedReturnProduct(initialProducts);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingPickupData, productdata, projectManagementStore]);

  useEffect(() => {
    if (
      open &&
      productdata &&
      productdata.length > 0 &&
      !existingPickupData &&
      projectManagementStore.selectedReturnProduct.length === 0
    ) {
      const initialProducts: UnpaidSampleProduct[] = productdata.map(
        (product) => {
          const receivedQty =
            projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products?.find(
              (p) => p.id === product.id,
            )?.quantity || 0;
          const actualQty = product.quantity || 0;
          const defaultReturnQty = Math.max(0, actualQty - receivedQty);

          return {
            id: product.id || 0,
            quantity: defaultReturnQty,
            is_selected: false,
            is_archived: false,
          };
        },
      );

      projectManagementStore.setselectedReturnProduct(initialProducts);

      projectManagementStore.setUnpaidSamplePickupUpdateFormData({
        ...projectManagementStore.unpaidSamplePickupUpdateFormData,
        products: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productdata, existingPickupData]);

  const formData = projectManagementStore.unpaidSamplePickupUpdateFormData;

  const getValidationState = () => {
    const hasBasicRequiredFields = formData.actual_pickup_date !== null;
    const hasPackReceivedDate = !!formData.actual_pack_received_date;
    const hasSelectedProducts =
      projectManagementStore.selectedReturnProduct.some(
        (p) => p.is_selected && p.quantity > 0,
      );
    const allSelectedProductsHaveQuantity =
      projectManagementStore.selectedReturnProduct
        .filter((p) => p.is_selected)
        .every((p) => p.quantity > 0);
    const hasAnySelectedProducts =
      projectManagementStore.selectedReturnProduct.some((p) => p.is_selected);
    const hadOriginalPackReceivedDate =
      existingPickupData?.actual_pack_received_date;

    const canSave = hasBasicRequiredFields;

    return {
      hasBasicRequiredFields,
      hasPackReceivedDate,
      hasSelectedProducts,
      allSelectedProductsHaveQuantity,
      hasAnySelectedProducts,
      hadOriginalPackReceivedDate,
      canSave,
    };
  };

  const validationState = getValidationState();

  const handleActualPickupDateChange = (date: Dayjs | null) => {
    formData.actual_pickup_date = date
      ? date.format("YYYY-MM-DD HH:mm:ss")
      : "";
  };

  const handlePackReceivedDateChange = (date: Dayjs | null) => {
    formData.actual_pack_received_date = date
      ? date.format("YYYY-MM-DD HH:mm:ss")
      : "";
  };

  const handleClose = () => {
    projectManagementStore.resetUnpaidSamplePickupUpdateFormData();
    projectManagementStore.setselectedReturnProduct([]);
    onClose();
  };

  const handleRemarksChange = (value: string) => {
    formData.product_remarks = value;
  };

  const existingImageList = React.useMemo(() => {
    if (formData.image_urls && Array.isArray(formData.image_urls)) {
      return formData.image_urls.map((url, index) => ({
        id: url.id,
        title: `Image ${index + 1}`,
        img: url.image_url,
      }));
    }
    return [];
  }, [formData.image_urls]);

  const handleImageUpload = (files: File[]) => {
    // Append new files to existing selectedFiles instead of replacing
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleRemoveLocalImage = (image: {
    id: string | number;
    title?: string;
    img: string;
  }) => {
    if (typeof image.id === "string" && image.id.startsWith("file-")) {
      const fileIndex = parseInt(image.id.replace("file-", ""));

      if (image.img.startsWith("blob:")) {
        URL.revokeObjectURL(image.img);
      }

      const updatedFiles = selectedFiles.filter(
        (_, index) => index !== fileIndex,
      );
      setSelectedFiles(updatedFiles);
    }
  };

  React.useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        const url = URL.createObjectURL(file);
        URL.revokeObjectURL(url);
      });
    };
  }, [selectedFiles]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      {userStore.hasPermission(EmployeeRoles.EDIT_UNPAID_RETURN_SAMPLES) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography variant="heading3">
              {`Unpaid Sample Post Pickup  (${orderId})`}
            </CustomTypography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.customColors.greyTextColor,
                "&:hover": {
                  backgroundColor: theme.palette.customColors.lightGreyColor,
                  color: theme.palette.customColors.blackBorder,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent
            sx={{
              padding: { xs: "20px", sm: "24px" },
              flex: 1,
              overflow: "auto",
              maxHeight: "60vh",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.customColors.greyBackground,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.customColors.greyTextColor,
                },
              },
            }}
          >
            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1, mb: 3 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Actual Pickup Date{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  The date when the samples were picked up
                </CustomTypography>
                <DatePicker2
                  type="date"
                  label="Select Date"
                  value={
                    formData.actual_pickup_date
                      ? dayjs(formData.actual_pickup_date)
                      : null
                  }
                  onChange={handleActualPickupDateChange}
                  zIndex={4000}
                />
              </Box>

              <Box sx={{ flex: 1, mb: 3 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Pack Received Date
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  The date when the samples were received
                </CustomTypography>
                <DatePicker2
                  type="date"
                  label="Select Date"
                  value={
                    formData.actual_pack_received_date
                      ? dayjs(formData.actual_pack_received_date)
                      : null
                  }
                  onChange={handlePackReceivedDateChange}
                  zIndex={4000}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 4 }}>
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 10 }}
              >
                Products
                {projectManagementStore.unpaidSamplePickupUpdateFormData
                  .actual_pack_received_date !== null && (
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                )}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                The products that were picked up
              </CustomTypography>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                  overflowX: { sm: "auto", xs: "auto" },
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                      }}
                    >
                      <AppTableHeaderCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "60px",
                          padding: "12px 10px 12px 20px",
                        }}
                      ></AppTableHeaderCell>

                      <AppTableHeaderCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "60px",
                          padding: "12px 8px",
                        }}
                      >
                        S.No
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          width: "80px",
                          padding: "12px 8px",
                        }}
                      >
                        ID
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          padding: "12px 24px",
                          textAlign: "left",
                        }}
                      >
                        Product
                      </AppTableHeaderCell>

                      <AppTableHeaderCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          textAlign: "center",
                          width: "150px",
                          padding: "12px 16px",
                        }}
                      >
                        Actual Quantity
                      </AppTableHeaderCell>
                      <AppTableHeaderCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.customColors.blackBorder,
                          textAlign: "center",
                          width: "150px",
                          padding: "12px 16px",
                        }}
                      >
                        Received Quantity
                      </AppTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedProductData.map((product, index) => {
                      const receivedQty =
                        projectManagementStore.unpaidSamplePickupUpdateInfoResponce?.products?.find(
                          (p) => p.id === product.id,
                        )?.quantity || 0;
                      const actualQty = product.quantity || 0;
                      const isFullyReceived = receivedQty >= actualQty;

                      return (
                        <TableRow key={product.id || `product_${index}`} hover>
                          <AppTableRowCell
                            sx={{
                              width: "60px",
                              padding: "12px 10px 12px 20px",
                            }}
                          >
                            {isFullyReceived && (
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                fontSize={"1.2rem"}
                                style={{
                                  color:
                                    theme.palette.customColors.successGreen,
                                }}
                              />
                            )}
                          </AppTableRowCell>

                          <AppTableRowCell
                            sx={{
                              width: "60px",
                              padding: "12px 8px",
                              textAlign: "center",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {index + 1}
                            </CustomTypography>
                          </AppTableRowCell>

                          <AppTableRowCell
                            sx={{
                              width: "80px",
                              padding: "12px 8px",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {product.id || "No product id"}
                            </CustomTypography>
                          </AppTableRowCell>
                          <AppTableRowCell
                            sx={{
                              padding: "12px 24px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <img
                                src={product.image_url}
                                alt={""}
                                style={{ width: "40px", height: "40px" }}
                              />
                              <CustomTypography variant="body2">
                                {product.name || "No product name"}
                              </CustomTypography>
                            </Box>
                          </AppTableRowCell>
                          <AppTableRowCell
                            sx={{
                              textAlign: "center",
                              width: "120px",
                              padding: "12px 16px",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {actualQty}
                            </CustomTypography>
                          </AppTableRowCell>
                          <AppTableRowCell
                            sx={{
                              textAlign: "center",
                              width: "120px",
                              padding: "12px 16px",
                            }}
                          >
                            <CustomTypography variant="body2">
                              {receivedQty}
                            </CustomTypography>
                          </AppTableRowCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider sx={{ my: 3 }} />
            {/* FIXED: Remarks section - now always allows updates */}
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                Remarks
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Any additional remarks or comments
              </CustomTypography>

              <TextField
                multiline={true}
                rows={3}
                fullWidth
                placeholder="Enter any remarks or additional information..."
                value={formData.product_remarks || ""}
                onChange={(e) => handleRemarksChange(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: theme.palette.customColors.greyDarkborder,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.customColors.blueGradientStart,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.customColors.blueGradientStart,
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Enhanced Image upload section - similar to AddProduct page */}
            <Box>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                Upload Images
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Upload images related to the pickup
              </CustomTypography>

              <Box sx={{ width: "100%", maxWidth: "100%" }}>
                <MediaAssetsUpload
                  uploadIcon={
                    <img
                      src={FileUploadIcon}
                      alt="Upload"
                      width={40}
                      height={40}
                    />
                  }
                  fileInfo="PNG, JPG up to 2MB each"
                  multiple={true}
                  existingImageList={existingImageList}
                  newImageList={selectedFiles}
                  deleteImage={onDeleteImage}
                  removeImage={handleRemoveLocalImage}
                  addImagesToBeUploaded={handleImageUpload}
                />
              </Box>
            </Box>
          </DialogContent>

          {/* Footer */}
          <DialogActions
            sx={{
              padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              gap: 2,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              {projectManagementStore?.unpaidSamplePickupUpdateInfoResponce
                ?.actual_pickup_modified_by_name && (
                <>
                  <CustomTypography variant="body3">
                    <span style={{ fontWeight: 600 }}>Last Updated by : </span>
                    {projectManagementStore.unpaidSamplePickupUpdateInfoResponce
                      ?.actual_pickup_modified_by_name || "Not yet updated"}
                  </CustomTypography>
                  <CustomTypography variant="body3" style={{ marginBottom: 5 }}>
                    <span style={{ fontWeight: 600 }}>Last Updated on : </span>
                    {projectManagementStore.unpaidSamplePickupUpdateInfoResponce
                      ?.modified_date
                      ? dayjs(
                          projectManagementStore
                            .unpaidSamplePickupUpdateInfoResponce.modified_date,
                        ).format("DD/MM/YYYY HH:mm")
                      : "Not yet updated"}
                  </CustomTypography>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.greyTextColor,
                  fontWeight: 600,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.customColors.greyTextColor,
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={async () => {
                  let uploadedImageUrls: string[] = [];
                  if (selectedFiles.length > 0) {
                    const imageAPI = new ImageAPI();
                    uploadedImageUrls = await imageAPI
                      .uploadImage(selectedFiles, "product-images")
                      .then((res) => {
                        setSelectedFiles([]);
                        return res;
                      });
                  }

                  const selectedProducts =
                    projectManagementStore.selectedReturnProduct
                      .filter((p) => p.is_selected && p.quantity > 0)
                      .map((p) => ({ id: p.id, quantity: p.quantity }));

                  const payload: any = {
                    ...formData,
                    products: selectedProducts,
                  };

                  // Remove image_urls from payload to avoid sending existing images
                  delete payload.image_urls;

                  // Only add image_url if there are new uploaded images
                  if (uploadedImageUrls.length > 0) {
                    payload.image_url = uploadedImageUrls;
                  } else {
                    // When no new images are uploaded, pass empty array
                    payload.image_url = [];
                  }

                  onSave(
                    payload,
                    Number(projectManagementStore.selectedReturnPickupId),
                  );
                }}
                variant="contained"
                disabled={!validationState.canSave}
                sx={{
                  backgroundColor: validationState.canSave
                    ? theme.palette.customColors.EmeraldGreen
                    : theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.white,
                  fontWeight: 600,
                  padding: "10px 24px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: validationState.canSave
                      ? theme.palette.customColors.greenDark
                      : theme.palette.customColors.greyDarkborder,
                    transform: validationState.canSave
                      ? "translateY(-1px)"
                      : "none",
                    boxShadow: validationState.canSave
                      ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                      : "none",
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.customColors.greyDarkborder,
                    color: theme.palette.customColors.white,
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

export default observer(UnpaidSamplePickupUpdatePopup);
